import { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Alert,
  Tabs,
  Typography,
  message,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { updateApp, type App } from '@/api/apps';

const { Text } = Typography;

/**
 * CopyEditor — apps.copy_json 的编辑界面.
 *
 * 设计:
 *  - "快速编辑" Tab: 8 个最常改的路径 (landing title / cta / paywall cta /
 *    mine 昵称 / errors), antd Form 字段.
 *  - "原始 JSON" Tab: monospace textarea, 高级用户直接编辑整份 JSON.
 *  - 保存逻辑: 以原始 JSON 为底, overlay 快速字段, PATCH /admin/v1/apps/:id.
 *    若原始 JSON 解析失败, 拒绝保存.
 *  - 实时校验: 原始 JSON 失败时红框 + 错误提示.
 *
 * 字段路径使用点分 (e.g. "landing.title"), set() helper 写嵌套 path.
 */

interface CopyEditorProps {
  app: App;
  onSaved: (next: App) => void;
}

// 高频路径 — 标签 + 字段 path + placeholder + 行数 (textarea / input).
const QUICK_FIELDS: Array<{
  path: string;
  label: string;
  rows?: number;
  placeholder?: string;
}> = [
  { path: 'appName', label: 'App 名 (面向用户)' },
  { path: 'landing.title', label: 'Landing 标题', rows: 1 },
  { path: 'landing.tagline', label: 'Landing 副标题' },
  { path: 'landing.ctaText', label: 'Landing 主按钮 (开始 / 开始我的占卜 / ...)' },
  { path: 'landing.hint', label: 'Landing 提示 (5 道题 · 首次免费 ...)' },
  { path: 'result.unlockBar.cta', label: '解锁 CTA (9.9 解锁 ...)' },
  { path: 'result.shareTitle', label: '分享标题' },
  { path: 'mine.sections.history', label: 'Mine 历史 Tab 名 (我的灵兽志 / 我的前世)' },
];

function getPath(obj: unknown, path: string): string {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj) as string ?? '';
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const keys = path.split('.');
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (cur[k] === undefined || typeof cur[k] !== 'object' || cur[k] === null) {
      cur[k] = {};
    }
    cur = cur[k] as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
}

export function CopyEditor({ app, onSaved }: CopyEditorProps) {
  const initialCopy = useMemo(
    () => (app.copy_json as Record<string, unknown>) || {},
    [app.id, app.updated_at]
  );

  // 原始 JSON textarea 状态 (string) + 解析状态
  const [rawText, setRawText] = useState(() => JSON.stringify(initialCopy, null, 2));
  const [rawError, setRawError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // app 变 (e.g. 别处编辑后回到本页) → 重置编辑器
  useEffect(() => {
    setRawText(JSON.stringify(initialCopy, null, 2));
    setRawError(null);
    const init: Record<string, string> = {};
    QUICK_FIELDS.forEach((f) => {
      init[f.path] = getPath(initialCopy, f.path);
    });
    form.setFieldsValue(init);
  }, [initialCopy, form]);

  const validateRaw = (text: string): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== 'object' || parsed === null) {
        setRawError('JSON 必须是对象');
        return null;
      }
      setRawError(null);
      return parsed as Record<string, unknown>;
    } catch (e) {
      setRawError(e instanceof Error ? e.message : 'JSON 解析失败');
      return null;
    }
  };

  const onSave = async () => {
    // 1. parse raw — 失败拒绝保存
    const base = validateRaw(rawText);
    if (!base) {
      message.error('原始 JSON 有语法错误, 修复后再保存');
      return;
    }
    // 2. overlay 快速字段
    const quickValues = form.getFieldsValue();
    QUICK_FIELDS.forEach((f) => {
      const v = quickValues[f.path];
      if (v !== undefined && v !== '') {
        setPath(base, f.path, v);
      }
    });
    setSaving(true);
    try {
      const next = await updateApp(app.id, { copy_json: base });
      onSaved(next);
      // 重新 sync raw text 以反映服务器规范化的格式
      setRawText(JSON.stringify(next.copy_json || {}, null, 2));
      message.success('Copy 已保存 — 用户 ≤30s 后看到新文案');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      extra={
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave}>
          保存 Copy
        </Button>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="编辑前端面向用户的文案. 保存后 HTTP cache (30s) 过期, 用户下次进页面看到新版本. 不需要 frontend rebuild."
      />
      <Tabs
        type="card"
        items={[
          {
            key: 'quick',
            label: '快速编辑 (高频字段)',
            children: (
              <Form form={form} layout="vertical">
                {QUICK_FIELDS.map((f) => (
                  <Form.Item key={f.path} label={`${f.label} ${pathTag(f.path)}`} name={f.path}>
                    {f.rows && f.rows > 1 ? (
                      <Input.TextArea rows={f.rows} placeholder={f.placeholder} />
                    ) : (
                      <Input placeholder={f.placeholder} />
                    )}
                  </Form.Item>
                ))}
                <Alert
                  type="warning"
                  message="只编辑快速字段时, 其他字段保持原值 (从下方原始 JSON 读取)."
                />
              </Form>
            ),
          },
          {
            key: 'raw',
            label: '原始 JSON (完整)',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Alert
                  type="warning"
                  showIcon
                  message="完整 copy_json. shape 见 frontend src/copy/types.ts SoulAnimalCopy. 解析失败 → 拒绝保存."
                />
                <Input.TextArea
                  value={rawText}
                  onChange={(e) => {
                    setRawText(e.target.value);
                    validateRaw(e.target.value);
                  }}
                  autoSize={{ minRows: 20, maxRows: 60 }}
                  style={{
                    fontFamily: 'ui-monospace, Menlo, monospace',
                    fontSize: 12,
                    borderColor: rawError ? '#ff4d4f' : undefined,
                  }}
                />
                {rawError && (
                  <Alert type="error" message={`JSON 错误: ${rawError}`} showIcon />
                )}
                {!rawError && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ✓ JSON 有效, {Math.round(rawText.length / 1024 * 10) / 10} KB
                  </Text>
                )}
              </Space>
            ),
          },
        ]}
      />
    </Card>
  );
}

function pathTag(p: string) {
  return <Text code style={{ fontSize: 10, color: '#999' }}>{p}</Text>;
}
